import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, EMPTY } from 'rxjs';
import { AssignmentStatus, AssignmentQuestion } from './assignment.models';
import { AssignmentApiService } from './assignment-api.service';
import { AssignmentStateService } from './assignment-state.service';

@Injectable({
  providedIn: 'root',
})
export class AssignmentQuestionsService {
  private readonly apiService = inject(AssignmentApiService);
  private readonly state = inject(AssignmentStateService);

  /**
   * Update all questions and persist to backend immediately.
   * Used for reorder, delete, and add operations on the question list.
   */
  updateQuestions(questions: AssignmentQuestion[]): void {
    this.state.setQuestions(questions);

    const assignmentId = this.state.assignmentId;
    if (!assignmentId) {
      return;
    }

    this.apiService
      .updateQuestions(assignmentId, questions)
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  /**
   * Replace a single question in local state without triggering a backend save.
   * Used by the edit question sheet after a successful save via the single-question endpoint.
   */
  replaceQuestionLocally(question: AssignmentQuestion): void {
    const questions = this.state.questions.map((q) =>
      q.id === question.id ? question : q,
    );
    this.state.setQuestions(questions);
  }

  /**
   * Save all questions to backend (used on navigation to Settings step).
   */
  saveQuestions(): Observable<void> {
    this.state.setError(null);

    const assignmentId = this.state.assignmentId;
    if (!assignmentId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No assignment ID'));
      });
    }

    return this.apiService
      .updateQuestions(assignmentId, this.state.questions)
      .pipe(
        map(() => {
          this.state.setStatus(AssignmentStatus.QUESTIONS_GENERATED);
          return undefined;
        }),
        catchError((err) => {
          this.state.setError(
            'Failed to save questions. Please try again.',
          );
          throw err;
        }),
      );
  }
}
